/**
 * Application Constants
 * Centralized numeric constants and magic numbers
 */

import type { TimerOption, OperationLimits, OperationConfig } from '../types'

// Storage keys - CRITICAL: DO NOT CHANGE - Required for backward compatibility
export const STORAGE_KEYS = {
  settings: 'flashcardFunSettings',
  score: 'flashcardFunStats',
  speech: 'flashcardSpeechSettings',
  settingsPanel: 'flashcardSettingsPanels',
  theme: 'flashcard-theme-preference',
} as const

// Timer configuration
export const TIMER = {
  VALID_VALUES: [0, 15, 30, 45, 60] as TimerOption[],
  DEFAULT_VALUE: 0 as TimerOption,
} as const

// Operation max value defaults
export const OPERATION_DEFAULTS = {
  MULTIPLICATION_MAX: 10,
  DIVISION_MAX: 10,
  ADDITION_MAX: 20,
  SUBTRACTION_MAX: 20,
} as const

// Operation max value limits (for slider bounds)
export const OPERATION_LIMITS: Record<string, OperationLimits> = {
  MULTIPLICATION: { min: 2, max: 20 },
  DIVISION: { min: 2, max: 20 },
  ADDITION: { min: 5, max: 50 },
  SUBTRACTION: { min: 5, max: 50 },
} as const

// Multiple choice generation
export const MULTIPLE_CHOICE = {
  OPTIONS_COUNT: 4,
  MAX_GENERATION_ATTEMPTS: 150,
  OFFSET_RATIO: 0.15,
  MIN_OFFSET_RANGE: 3,
  FILLER_LIMIT: 32,
  DEFAULT_MIN_BOUND: 0,
  DEFAULT_MAX_BOUND: 100,
  MIN_REASONABLE_BOUND: 10,
} as const

// Animation and UI timing (in milliseconds)
export const TIMING = {
  FLASH_CARD_RESET_DELAY: 900,
  CORRECT_ANSWER_DELAY: 1200,
  SKIP_ANSWER_DELAY: 1000,
  TRANSCRIPTION_TIMEOUT: 15000,
  RECORDING_STOP_DELAY: 300,
} as const

// Confetti celebration
export const CONFETTI = {
  DEFAULT_DURATION: 1800,
  MIN_DURATION: 1000,
  MAX_ADDITIONAL_DURATION: 3000,
  STREAK_DURATION_FACTOR: 600,
  PARTICLE_DENSITY: 6, // width / density = particle count
  MAX_PARTICLES: 140,
  GRAVITY: 0.02,
  RESPAWN_OFFSET: 20,
} as const

// Celebration horn audio
export const HORN = {
  START_FREQUENCY: 440,
  PEAK_FREQUENCY: 880,
  END_FREQUENCY: 660,
  ATTACK_TIME: 0.02,
  PEAK_TIME: 0.25,
  DECAY_TIME: 0.5,
  RELEASE_TIME: 0.7,
  INITIAL_GAIN: 0.0001,
  PEAK_GAIN: 0.5,
} as const

// Word practice
export const WORD_PRACTICE = {
  MIN_DIFFICULTY: 1,
  MAX_DIFFICULTY: 10,
  PREVIEW_COUNT: 10,
} as const

// Letters practice
export const LETTERS_PRACTICE = {
  LEVELS: [1, 2, 3] as const,
} as const

// Listening/speech recognition
export const LISTENING = {
  DEFAULT_MAX_DIFFICULTY: 5,
  DEFAULT_IDLE_TIMEOUT: 4000,
  DEFAULT_MAX_DURATION: 7000,
} as const

// Number parsing
export const PARSING = {
  RADIX: 10,
} as const

// Font size options
export const FONT_SIZE_OPTIONS = ['small', 'medium', 'large', 'xlarge'] as const

// Font size maps
export const QUESTION_FONT_MAP = {
  small: '3.6rem',
  medium: '5rem',
  large: '6.5rem',
  xlarge: '8rem',
} as const

export const ANSWER_FONT_MAP = {
  small: '1.8rem',
  medium: '2.5rem',
  large: '3.25rem',
  xlarge: '4rem',
} as const

// Praise messages
export const PRAISE_MESSAGES = [
  'Blast off! 🚀',
  'Out of this world!',
  'Stellar work!',
  "You're flying high!",
  'Mission accomplished!',
  'Orbit achieved!',
  'Perfect trajectory!',
  'Cosmic success!',
] as const

// Encouragement messages
export const ENCOURAGEMENT_MESSAGES = [
  'Not quite in orbit yet...',
  'Adjusting trajectory...',
  'Try again, astronaut!',
  'Almost there, space cadet!',
] as const

// Operation configurations
export const OPERATION_CONFIGS: OperationConfig[] = [
  {
    id: 'multiplication',
    label: 'Multiplication ×',
    hint: 'Times tables 0–10',
    defaultChecked: true,
  },
  {
    id: 'division',
    label: 'Division ÷',
    hint: 'Perfect division from the 0–10 tables',
    defaultChecked: true,
  },
  {
    id: 'addition',
    label: 'Addition +',
    hint: 'Friendly sums up to 20',
    defaultChecked: false,
  },
  {
    id: 'subtraction',
    label: 'Subtraction −',
    hint: 'Never drops below zero',
    defaultChecked: false,
  },
]

// OpenAI masked placeholder
export const MASKED_API_KEY_PLACEHOLDER = '••••••••••'
